import React, { useCallback } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import type { LoadOptions } from "react-select-async-paginate";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../store";
import { fetchSalesReps } from "../../store/slices/userSlice";

export interface OptionType {
  label: string;
  value: string;
  data?: any; // Original sales rep object
}

interface SalesRepSelectProps {
  userId?: string; // The cseId from parent/params
  value?: OptionType | null; // Selected option object
  onChange?: (option: { _id: string; username: string } | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const SalesRepSelect: React.FC<SalesRepSelectProps> = ({
  userId,
  value,
  onChange,
  placeholder = "Select Sales Rep...",
  disabled = false,
  className,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const limit = 10;

  // AsyncPaginate loadOptions handler
  const loadOptions: LoadOptions<OptionType, any, { page: number }> =
    useCallback(
      async (search, prevOptions, additional) => {
        // 1. Guard clause: Do not fetch if userId is not provided
        if (!userId) {
          return {
            options: [],
            hasMore: false,
            additional: { page: 1 },
          };
        }

        const page = additional?.page || 1;

        try {
          // 2. Dispatch thunk and unwrap the payload directly
          const result = await dispatch(
            fetchSalesReps({
              userId,
              params: {
                search: search.trim(),
                page,
                limit,
              },
            }),
          ).unwrap();

          const rawReps = result?.data || [];
          const pagination = result?.pagination;

          // 3. Map API response data into OptionType
          const options: OptionType[] = rawReps.map((rep: any) => ({
            label: `${rep.name} (${rep.mobileNumber})`,
            value: rep._id,
            data: rep,
          }));

          const totalPages = pagination?.totalPages || 1;
          const hasMore = page < totalPages;

          return {
            options,
            hasMore,
            additional: {
              page: page + 1,
            },
          };
        } catch (error) {
          return {
            options: [],
            hasMore: false,
            additional: { page },
          };
        }
      },
      [dispatch, userId],
    );

  return (
    <AsyncPaginate
      // Keying by userId forces AsyncPaginate to clear options & cache when userId changes
      key={userId || "no-user"}
      value={value}
      loadOptions={loadOptions}
      onChange={(option) => {
        if (onChange) {
          onChange(
            option ? { _id: option.value, username: option.label } : null,
          );
        }
      }}
      isDisabled={disabled || !userId}
      placeholder={!userId ? "Select user first..." : placeholder}
      className={className}
      isClearable
      debounceTimeout={400}
      additional={{
        page: 1,
      }}
    />
  );
};
